import { z } from "zod";
import { Config, Flags, Logger, ConfigParams, overrideLogger } from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.5";
import { Session } from "./domain/Session";
import { ArcwareSettingsSchema, Settings } from "./domain/ArcwareSettingsSchema";

/** Default arcware signalling endpoint. */
export const DefaultUrl = `wss://signalling-client.ragnarok.arcware.cloud` as const;

// The below Logger overrides can likely be removed as PSInfra 5.5 logger supports setting log verbosity 
// and these overrides are matching the default logging behaviour anyway (except info).
/*Logger.Info = (message: string) => {
  // console.info(message);
};
Logger.Warning = (message: string) => {
  console.warn(message);
};
Logger.Error = (message: string) => {
  if (message?.startsWith("unhandled Stat Type")) return;
  console.error(message);
};*/
Logger.InitLogging(2,false);

export interface ArcwareConfigParams extends ConfigParams {
  settings: Settings;
}

export class ArcwareConfig extends Config {
  /**
   * Can be used to fetch projectId and shareId from the current url.
   * Example:
   * /something/:projectId/:shareId/something?something => { projectId, shareId }
   * /:projectId => { projectId }
   * /:shareId => { shareId }
   * /:shareId/:projectId => { projectId, shareId }
   *
   * The projectId must be uuidv4.
   * The shareId must be "shareId-" prefixed.
   *
   * Usage:
   * // This will spread the projectId and the shareId into the settings. Define anything you want to override after the spread operation.
   * new ArcwareConfig({ settings: { ...ArcwareConfig.PickIdsOfUrl() } });
   */
  public static PickIdsOfUrl(): Pick<Settings, "shareId" | "projectId"> {
    const { pathname } = new URL(window.location.href);
    const sections = pathname.split("/");
    const projectId = sections.find((section) => z.string().uuid().safeParse(section).success);
    const shareId = sections.find((section) => z.string().startsWith("share-").safeParse(section).success);
    return {
      projectId,
      shareId,
    };
  }

  public readonly session: Session;

  public readonly settings: Settings;

  private _initialSettings;

  public readonly VERSION = "1.1.22";

  constructor(config: ArcwareConfigParams) {
    const dfToken = new URL(window.location.href).search
      .slice(1)
      .split("&")
      .find((qp) => qp.startsWith("df-"));
    if (!dfToken && !config.settings.shareId && !config.settings.projectId) {
      throw new Error(`ShareId or ProjectId must be specified.`);
    }

    // Validate settings input.
    const settings = ArcwareSettingsSchema.parse(config.settings);

    // Setup arcware default settings.
    if (!config.initialSettings) config.initialSettings = {};
    if (!config.initialSettings?.hasOwnProperty("AutoConnect")) config.initialSettings.AutoConnect = false;
    // if (!config.initialSettings?.hasOwnProperty("HoveringMouse")) config.initialSettings.HoveringMouse = true;
    if (!config.initialSettings?.hasOwnProperty("AFKTimeout")) config.initialSettings.AFKTimeout = 600;
    if (!config.initialSettings.ss) config.initialSettings.ss = DefaultUrl;

    super(config);
    this.settings = settings;
    this.session = new Session();
    this._initialSettings = config.initialSettings;

    // Setup arcware default settings.

    this.setFlagEnabled(Flags.AFKDetection, true);

    // IMPORTANT NOTE: The feature BrowserSendOffer is no longer supported in PSInfra 5.5.
    // The offer is now expected to come from UE. Ensure that your signalling server supports this behaviour.

    //this.setFlagEnabled(Flags.BrowserSendOffer, true);
  }

  /** Setup connection string. */
  public get urlFlags(): string {
    let flags = "";

    // Setup connection via shareId.
    if (this.settings.shareId) flags += `&sh=${this.settings.shareId}`;
    // Additionally setup the projectId property if the shareId is set up with multiple projects.
    if (this.settings.projectId) flags += `&p=${this.settings.projectId}`;

    // Setup direct session connection.
    if (typeof this.settings.session === "string") {
      flags += `&s=${this.settings.session}`;
    } else {
      // Setup session reconnect.
      const sessionId = this.session.id;
      if (sessionId) {
        flags += `&s=${sessionId}`;
        // If a sessionId is fetched out of the Session class, it's a reconnection attempt.
        flags += `&r=true`;
      }
    }

    // Setup bypass flag.
    if (this.settings.bypass) flags += `&b=true`;

    // Setup authentication token for platform internal previewing.
    if (this.settings.token) flags += `&t=${this.settings.token}`;

    const query = new URLSearchParams(window.location.search);

    // Fetch rId parameter from the url and pass it to the connection string.
    const rId = query.get("rId") ?? undefined;
    if (rId) flags += `&rId=${rId}`;

    // Setup DirectFlow.
    const dfToken = new URL(window.location.href).search
      .slice(1)
      .split("&")
      .find((qp) => qp.startsWith("df-"));
    if (dfToken) flags += `&${dfToken}`;

    return flags;
  }

  public get initialSettings() {
    return this._initialSettings;
  }

  public modifyInitialSettings(mouseLock: boolean): void {
    this.setFlagEnabled(Flags.HoveringMouseMode, mouseLock);
  }
}
